import React from 'react'
import { Trans } from '@lingui/macro'
import { useDispatch, useSelector } from 'react-redux'
import { ButtonLink } from 'components/UI/ButtonLink/ButtonLink'
import { getColor } from 'components/utils'
import { Alert } from 'components/UI/Alert/Alert'
import { IdProductsState } from 'model/dashboardProducts/dProductsReducer'
import { ExclamationCircleIcon, MinusSmIcon } from '@heroicons/react/outline'
import { InputCheckbox } from 'components/UI/InputCheckbox/InputCheckbox'
import {
    dProductsCheckedAllItemAC,
    dProductsCheckedItemAC,
    dProductsSetDetailItemAC
} from 'model/dashboardProducts/dProductsActionCreators'
import { typeDataProductInfoBulkActions } from '../index'
import {
    DotsVerticalIcon,
} from '@heroicons/react/outline'
import { numberCurrencyFormat } from 'constants/convertToLocalCurrency';
import { ISystemState } from 'model/system/systemReducer';
import { POSITION_ALLOW_FULL_CRUD_PRODUCTS, USERPOSITIONS } from 'constants/userPositions';
import { psidValidationEnv } from 'components/containers/dashboard/dataProducts/constants';


// Type
export type typeTableHeader = {
    variant?: string
    bulkOperations: typeDataProductInfoBulkActions
}


// Header data component
export const TableHeader: React.FC<typeTableHeader> = ({ variant, bulkOperations }) => {

    const color = getColor(variant)

    const dispatch = useDispatch()

    //const {value: selectAllChecked, changeValueHandler: changeSelectAllChecked, setValue: setSelectAllChecked} = useCheckboxChange(false)
    const tableData = useSelector((state: { dProducts: IdProductsState }) => state.dProducts.data)

    const groupActions = tableData.find(item => item.checked)

    const checkedAll = () => {
        dispatch(dProductsCheckedAllItemAC(true))
    }

    const unCheckedAll = () => {
        dispatch(dProductsCheckedAllItemAC(false))
    }

    return (
        <>
            { !groupActions
                ? <>
                    <th scope="col"
                        className="px-4 py-3  text-xs font-medium uppercase tracking-wider text-center">
                        <InputCheckbox id="selectAll" checked={ false } onChange={ () => checkedAll() }/>
                    </th>
                    <th scope="col"
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                        <Trans>PSID/ID</Trans>
                    </th>
                    <th scope="col"
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                        <Trans>Name</Trans>
                    </th>
                    <th scope="col"
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                        <Trans>Category</Trans>
                    </th>
                    <th scope="col"
                        className="px-4 py-3  text-xs font-medium uppercase tracking-wider text-center">
                        <Trans>Prices</Trans>
                    </th>
                    <th scope="col"
                        className="px-4 py-3  text-xs font-medium uppercase tracking-wider text-center">
                        <Trans>VAT</Trans> %
                    </th>
                    <th scope="col"
                        className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-center">
                        <Trans id={'shortMarked'}>Marked</Trans>
                    </th>
                    <th scope="col"
                        className="px-4 py-3  text-xs font-medium uppercase tracking-wider text-center">
                        <Trans>Stores</Trans>
                    </th>
                    <th scope="col" className="w-1">

                    </th>
                </>
                : <>
                    <th scope="col"
                        className=" px-4 py-3  text-xs font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer">
                        <div className="flex justify-center" onClick={ () => unCheckedAll() }>
                            <div
                                className={ `flex flex-col justify-center items-center h-4 w-4 text-white focus:ring-white border border-gray-100 rounded bg-${ color }-600` }>
                                <div className="h-0.5 w-2 my-1 bg-white rounded"></div>
                            </div>
                        </div>
                    </th>
                    <th colSpan={ 8 } className="py-3 text-sm">
                        <div className="flex divide-x divide-gray-300">
                            { bulkOperations.map((item, index) => <ButtonLink key={ index } dark={ true }
                                                                              buttonClassName="px-3 font-medium text-gray-700"
                                                                              type="button" label={ item.title }

                                                                              onClick={ () => item.action() }/>) }
                        </div>
                    </th>
                </>
            }

        </>
    )
}


// Type
export type typeAction = {
    title: JSX.Element,
    disableSwitcher?: boolean,
    action: (id: string) => void,
}

export type typeTableBody = {
    variant?: string
    setOpenPopupDetails: React.Dispatch<React.SetStateAction<boolean>>
    rowsActions: typeAction[]
}


// Body data component
export const TableBody: React.FC<typeTableBody> = ({ variant, setOpenPopupDetails, rowsActions }) => {

    const color = getColor(variant)

    const dispatch = useDispatch()

    const tableData = useSelector((state: { dProducts: IdProductsState }) => state.dProducts.data)
    const position = useSelector((state: { system: ISystemState }) => state.system.userInfo?.position)

    const onChangeSelectItemHandler = (id: string) => {
        dispatch(dProductsCheckedItemAC(id))
    }

    const onCellClickHandler = (e: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>, id: string) => {
        dispatch(dProductsSetDetailItemAC(id))
        setOpenPopupDetails(true)
    }


    let component

    if (tableData.length) {
        component = tableData.map((item, index) => {

            const storeList= item?.stores.filter(store=>!store.store.deleted)
            let minPrice = 0, maxPrice = 0
            if (item.stores && storeList.length) {
                minPrice = storeList[0].price
                maxPrice = storeList[0].price
                storeList.forEach(storeItem => {
                    if (storeItem.price < minPrice) minPrice = storeItem.price
                    if (storeItem.price > maxPrice) maxPrice = storeItem.price
                })
            }
            const psid = psidValidationEnv ? ( !item.psid || item.psid === 'invalid' ) ?
                    <span className="text-red-400 flex gap-2"><ExclamationCircleIcon className="w-4 h-4"/><Trans>invalid PSID (need to check)</Trans></span> : item.psid
                : (!item.psid  || item!.psid === 'invalid') ? '-':item.psid

            return (
                <tr key={ item.id }
                    className={ `${ index % 2 === 0 ? 'bg-white' : 'bg-gray-50' } hover:bg-${ color }-50` }>

                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex justify-center">
                            <InputCheckbox
                                id={ 'select-' + item.id }
                                checked={ item.checked }
                                onChange={ () => onChangeSelectItemHandler(item.id) }
                            />
                        </div>
                    </td>


                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
                        onClick={ (e) => onCellClickHandler(e, item.id) }>
                        <div className="flex flex-col divide-y">
                            <span className="font-medium text-xs pb-1">{ psid }</span>
                            <span className={ 'text-xs pt-1' }>{ item.id }</span>
                        </div>
                    </td>

                    <td className={ `px-4 py-3 whitespace-nowrap text-sm ${ item.deleted ? '' : 'font-medium ' }text-gray-900 cursor-pointer` }
                        onClick={ (e) => onCellClickHandler(e, item.id) }>
                        { item.name }{ item.deleted ? ' ' : null }{ item.deleted ?
                        <span className="text-xs text-gray-400 font-normal"><Trans>(deleted)</Trans></span> : null }
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
                        onClick={ (e) => onCellClickHandler(e, item.id) }>
                        { item.category ? item.category.name :
                            <div className="flex "><MinusSmIcon className="h-5 w-5 text-gray-400"/>
                            </div> }
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 cursor-pointer"
                        onClick={ (e) => onCellClickHandler(e, item.id) }>

                        { minPrice && maxPrice
                            ? <div className="flex flex-col justify-center divide-y">
                                { minPrice !== maxPrice
                                    ? <>
                                        { minPrice > 0 ? <span
                                            className="font-medium"><Trans>from</Trans> { numberCurrencyFormat(+minPrice.toFixed(2)) }</span> : null }
                                        { maxPrice ? <span
                                            className="font-medium"><Trans>to</Trans> { numberCurrencyFormat(+maxPrice.toFixed(2)) }</span> : null }
                                    </>
                                    :
                                    <span className="font-medium">{ numberCurrencyFormat(+maxPrice.toFixed(2)) }</span>
                                }

                            </div>
                            :
                            <div className="flex justify-center"><MinusSmIcon className="h-5 w-5 text-gray-400"/></div>
                        }

                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-center cursor-pointer"
                        onClick={ (e) => onCellClickHandler(e, item.id) }>
                        {item.vat !==null ? item.vat%1>0 ? item.vat.toFixed(2):item.vat : '-' }
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-center cursor-pointer"
                        onClick={ (e) => onCellClickHandler(e, item.id) }>
                        { item.marked
                            ? <Trans>Yes</Trans>
                            : undefined
                        }
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-center cursor-pointer"
                        onClick={ (e) => onCellClickHandler(e, item.id) }>
                        { item.stores && storeList.length
                            ? storeList.length
                            : <div className="flex justify-center">
                                <MinusSmIcon className="h-5 w-5 text-gray-400"/>
                            </div>
                        }
                    </td>

                    <td className="whitespace-nowrap text-sm text-gray-500 pr-2">
                        <div className="relative flex">{ POSITION_ALLOW_FULL_CRUD_PRODUCTS.includes(position as USERPOSITIONS)  &&
                            <div className="row-action-block">
                                <DotsVerticalIcon className="row-action-list-open-button"/>
                                <div className="row-action-list">
                                    <div className="flex gap-x-4">
                                        { rowsActions.map((actionItem, ActionIndex) => {
                                            const link = <ButtonLink key={ ActionIndex } type={ 'button' }
                                                                     label={ actionItem.title }
                                                                     onClick={ () => actionItem.action(item.id) }/>
                                            if ('id' in actionItem) {
                                                // @ts-ignore
                                                switch (actionItem.id) {
                                                    case 'delete':
                                                        if (!item.deleted) return link
                                                        break
                                                    case 'recover':
                                                        if (item.deleted) return link
                                                        break
                                                    default:
                                                        return null
                                                }
                                            } else {
                                                return item.deleted ? null : link
                                            }
                                            return null
                                        }) }
                                    </div>
                                </div>
                            </div> }
                        </div>
                    </td>

                </tr>
            )
        })
    } else {
        component = <tr>
            <td className="p-2" colSpan={ 8 }>
                <Alert className="w-full" type={ 'info' }
                       title={ <Trans>The product list is empty. Click on the Add button to add a new product. Or check
                           filters if you applied them.</Trans> }/>
            </td>
        </tr>
    }

    return (
        <>
            { component }
        </>
    )
}
